import { Badge, Box, Button, Stack, Typography } from "@mui/material";
import { useStyles } from "./style";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import CatalogAccordion from "../CatalogAccordion";
import { MAX_SERVICES_PER_BOOKING } from "common/constant";
import {
  ICategories,
  IStaffs,
  ISelectedService,
  IBookingSettings,
} from "pages/booking/interface";
import { UserIcon } from "svgr/user";
import ServicesDialog from "./components/ServicesDialog";
import ServicesBox from "./components/ServicesBox";
interface IProps {
  categories: ICategories;
  staffs: IStaffs[];
  setActiveTab: (agr: number) => void;
  selectedServices: Array<ISelectedService>;
  setSelectedServices: (agr: Array<ISelectedService>) => void;
  bookingSettings: IBookingSettings;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
}

const PickServiceAndStaff = ({
  categories,
  staffs,
  setActiveTab,
  selectedServices,
  setSelectedServices,
  bookingSettings,
  step,
  setStep,
}: IProps) => {
  const classes = useStyles();
  // const [step, setStep] = useState(0);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const selectedServicesRef = useRef<HTMLDivElement>(null);
  const listServicesRef = useRef<HTMLDivElement>(null);

  const { allowMultipleServices, onlySingleStaffMember, showServicePrice } =
    bookingSettings;
  const maxServicesAllow = allowMultipleServices ? MAX_SERVICES_PER_BOOKING : 1;

  const handlePickStaffForService =
    (staffId: string, staffName: string) => () => {
      setSelectedServices(
        selectedServices.map((service, index) =>
          index === selectedServices.length - 1
            ? { ...service, staffId, staffName }
            : service
        )
      );
      setStep(2);
    };

  const handleRemoveService = (serviceId: string, index: number) => {
    setSelectedServices(
      selectedServices.filter(
        (service, idx) => !(service.serviceId === serviceId && idx === index)
      )
    );
  };

  const backToFirstStep = () => {
    setStep(0);
  };

  const nextToSecondTab = () => {
    setActiveTab(1);
  };

  const renderSelectedServices = () => {
    return (
      <Box
        className={classes.selectedServices}
        ref={selectedServicesRef}
        height={
          selectedServices.length < MAX_SERVICES_PER_BOOKING
            ? "auto"
            : "calc(100% - 110px)"
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            selectedServices.length === maxServicesAllow
              ? "space-between"
              : "center"
          }
          mb="15px"
        >
          {selectedServices.length === maxServicesAllow && (
            <Typography color="#061340" fontWeight={600} fontSize={18}>
              YOUR SERVICES
            </Typography>
          )}
          {step === 0 && (
            <Button className={classes.nextButton} onClick={nextToSecondTab}>
              Next
            </Button>
          )}
        </Stack>
        {selectedServices.length === maxServicesAllow || step === 2 ? (
          <ServicesBox
            services={selectedServices}
            onRemove={step === 0 ? handleRemoveService : undefined}
            sx={{ padding: 1.5, border: "1px solid #0B70E1" }}
            maxServicesAllow={maxServicesAllow}
            bookingSettings={bookingSettings}
          />
        ) : (
          <Typography fontSize={12} className={classes.helperText}>
            Choose another service below, or click "Next" above to pick a date
            and time for your appointment.
          </Typography>
        )}
        <ServicesDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          services={selectedServices}
          onRemove={handleRemoveService}
          bookingSettings={bookingSettings}
        />
      </Box>
    );
  };

  const filteredStaffs = staffs.filter(
    (staff) =>
      staff.productId ===
        selectedServices[selectedServices.length - 1]?.serviceId &&
      (!onlySingleStaffMember ||
        !selectedServices[0]?.staffId ||
        staff.staffId === selectedServices[0]?.staffId)
  );

  const filteredCategory = categories
    .filter((item) => !!item.services.length)
    // Filter category based on booking settings
    // Return all if only single staff is false or selected services is null
    .filter(
      (item) =>
        !onlySingleStaffMember ||
        !selectedServices.length ||
        // Get category by first staff id
        item.services.some(
          (service) =>
            (service?.staffs || []).some(
              (staff) => staff.staffId === selectedServices[0]?.staffId
            ) &&
            // hide category if it has one service and it already exists in selected
            !selectedServices.some((ser) => ser.serviceId === service.productId)
        )
    );

  useEffect(() => {
    if (selectedServicesRef?.current) {
      const { clientHeight = 0 } = selectedServicesRef?.current;

      if (listServicesRef.current)
        listServicesRef.current.style.height = `calc(100% - ${clientHeight}px)`;
    } else if (listServicesRef.current) {
      listServicesRef.current.style.height = "100%";
    }
  }, [selectedServicesRef?.current?.clientHeight]);

  useEffect(() => {
    if (!selectedServices.length) setOpenModal(false);
  }, [selectedServices.length]);

  return (
    <Box px={2} height="100%" position="relative">
      {!!selectedServices.length &&
        selectedServices.length < maxServicesAllow &&
        step === 0 && (
          <Box className={classes.floatingBox}>
            <Badge
              badgeContent={selectedServices.length}
              showZero
              overlap="circular"
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: "#FF0000",
                },
              }}
            >
              <Button
                className={classes.floatingButton}
                onClick={() => setOpenModal(true)}
              >
                selected
              </Button>
            </Badge>
          </Box>
        )}
      {step === 0 ? (
        <Box
          className={classes.animationContainer}
          sx={{ animation: "0.75s slide-right" }}
        >
          {selectedServices.length > 0 && renderSelectedServices()}
          {selectedServices.length < maxServicesAllow && (
            <Stack
              overflow="auto"
              width="100%"
              height="100%"
              gap={2}
              ref={listServicesRef}
              sx={{
                "&::after": {
                  content: '""',
                  display: !selectedServices.length ? "none" : "block",
                  minHeight: "50px",
                },
              }}
            >
              {filteredCategory.length ? (
                filteredCategory.map((category) => (
                  <CatalogAccordion
                    key={category.categoryId}
                    category={category}
                    setStep={setStep}
                    selectedServices={selectedServices}
                    setSelectedServices={setSelectedServices}
                    bookingSettings={bookingSettings}
                    ownerInfo={
                      staffs.find((item) => item.role === "Owner") || null
                    }
                  />
                ))
              ) : (
                <Typography
                  color="#637381"
                  textAlign="center"
                  fontWeight={600}
                  mt={2}
                >
                  No Service Available!
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      ) : step === 1 ? (
        <Stack
          className={classes.animationContainer}
          sx={{ animation: "0.75s slide-left" }}
        >
          <Stack height="100%" overflow="auto" gap={2}>
            {[
              ...filteredStaffs.filter((staff) => staff.role === "Owner"),
              ...filteredStaffs.filter((staff) => staff.role !== "Owner"),
            ].map((staff) => (
              <Stack
                key={staff.staffId}
                className={classes.staffRow}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={handlePickStaffForService(
                  staff.staffId,
                  staff.staffName
                )}
              >
                <UserIcon />
                <Typography
                  fontWeight={500}
                  color={staff.role === "Owner" ? "#FF0000" : "#061340"}
                >
                  {staff.staffName}
                </Typography>
                {/* {showServicePrice && (
                  <Typography
                    sx={{ marginLeft: "auto" }}
                    color="#061340"
                    fontWeight={700}
                  >
                    $
                    {selectedServices[selectedServices.length - 1].servicePrice}
                  </Typography>
                )} */}
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Box
          className={classes.animationContainer}
          sx={{ animation: "0.75s slide-left" }}
        >
          {selectedServices.length > 0 && renderSelectedServices()}
          <Stack alignItems="center">
            <Typography>Do you want to add another service?</Typography>
            <Stack direction="row" gap="10px" mt="20px">
              <Button
                className={classes.actionButton}
                onClick={backToFirstStep}
              >
                Yes
              </Button>
              <Button
                className={classes.actionButton}
                onClick={nextToSecondTab}
              >
                No
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PickServiceAndStaff;
